import { useId, type ComponentPropsWithoutRef } from "react";

import { cn } from "../../lib/cn";

export type TextareaProps = ComponentPropsWithoutRef<"textarea"> & {
  label: string;
  helperText?: string;
  error?: string;
};

export function Textarea({
  label,
  helperText,
  error,
  id,
  rows = 4,
  className,
  ...rest
}: TextareaProps) {
  const autoId = useId();
  const textareaId = id ?? autoId;
  const describedById = error
    ? `${textareaId}-error`
    : helperText
      ? `${textareaId}-help`
      : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={textareaId} className="text-label text-text">
        {label}
      </label>
      <textarea
        id={textareaId}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedById}
        className={cn(
          "bg-surface text-body text-text placeholder:text-placeholder block w-full rounded-md border border-separator px-3 py-2.5",
          "focus:ring-accent focus:outline-none focus:ring-2 focus:ring-inset",
          error && "ring-error ring-1 ring-inset",
          className,
        )}
        {...rest}
      />
      {error ? (
        <p id={`${textareaId}-error`} className="text-caption text-error-text">
          {error}
        </p>
      ) : helperText ? (
        <p
          id={`${textareaId}-help`}
          className="text-caption text-text-secondary"
        >
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
